/**
 * Serbian for API text of the v1 area, keyed by the exact English text the API sends: series labels, feature and
 * funnel names, breakdown dimensions, notes, unavailability reasons and source reasons of api/src/analyticsService.ts,
 * productMetrics.ts, warehouse/productModel.ts and warehouse/bigQueryWarehouse.ts. The mock data source uses the same
 * English text, so it is covered too.
 */
export const text: Record<string, string> = {
  // Series and chart labels
  'Daily active users': 'Dnevno aktivni korisnici',
  'Receipt scans': 'Skeniranja računa',
  'Receipts': 'Računi',
  'Price comparison': 'Poređenje cena',
  'Actual GCP cost': 'Stvarni GCP trošak',
  'Estimated cost': 'Procenjeni trošak',
  'Image receipts processed': 'Obrađeni računi sa fotografije',
  'Processed': 'Obrađeno',
  'Successful': 'Uspešno',
  'Failed': 'Neuspešno',
  'Requests': 'Zahtevi',
  'Searches': 'Pretrage',
  'No result': 'Bez rezultata',
  'AI cost': 'AI trošak',
  'Actual daily cost': 'Stvarni dnevni trošak',
  'Estimated daily cost': 'Procenjeni dnevni trošak',
  'Crashes': 'Padovi',
  'Non-fatal': 'Bez pada',
  'Receipt import p95': 'Uvoz računa p95',
  'Receipt p95': 'Račun p95',
  'Price comparison p95': 'Poređenje cena p95',
  'Price search p95': 'Pretraga cena p95',
  'New installs': 'Nove instalacije',
  'New installs (all devices)': 'Nove instalacije (svi uređaji)',
  'Active devices': 'Aktivni uređaji',
  'Uninstall signals': 'Signali deinstalacije',
  'Inactive 7+ days (seen in the prior 30)': 'Neaktivni 7+ dana (viđeni u prethodnih 30)',

  // Feature adoption (Overview)
  'Receipt': 'Račun',
  'Pantry': 'Ostava',
  'Budget': 'Budžet',
  'Shopping': 'Kupovina',
  'Shopping List': 'Lista za kupovinu',
  'Price Comparison': 'Poređenje cena',
  'Check-in': 'Provera namirnica',

  // Features, funnels and steps (warehouse/productModel.ts)
  'Shopping list': 'Lista za kupovinu',
  'QR receipt scan': 'Skeniranje računa preko QR koda',
  'Photo receipt scan': 'Skeniranje računa sa fotografije',
  'Shared into Aruha': 'Podeljeno u Aruhu',
  'Price search': 'Pretraga cena',
  'Food check-in': 'Provera namirnica',
  'Reminders': 'Podsetnici',
  'Receipt scan': 'Skeniranje računa',
  'Opened the list': 'Otvorena lista',
  'Added an item': 'Dodata stavka',
  'Checked an item': 'Označena stavka',
  'Completed shopping': 'Završena kupovina',
  'Started a QR scan': 'Započeto skeniranje QR koda',
  'Scan succeeded': 'Skeniranje uspelo',
  'Started a photo scan': 'Započeto skeniranje fotografije',
  'Opened Budget': 'Otvoren Budžet',
  'Created a budget': 'Napravljen budžet',
  'Added an expense': 'Dodat trošak',
  'Opened Price comparison': 'Otvoreno Poređenje cena',
  'Started a search': 'Započeta pretraga',
  'Search returned results': 'Pretraga je vratila rezultate',
  'Opened Pantry': 'Otvorena Ostava',
  'Marked an item as used': 'Stavka označena kao potrošena',
  'Opened the shopping list': 'Otvorena lista za kupovinu',
  'Added a shopping item': 'Dodata stavka na listu za kupovinu',
  'Started a receipt scan': 'Započeto skeniranje računa',
  'Scanned a receipt': 'Skeniran račun',
  'Added a pantry item': 'Dodata stavka u Ostavu',
  'Searched prices': 'Pretražene cene',
  'Finished a pantry check': 'Završena provera Ostave',
  'Finished a food check-in': 'Završena provera namirnica',

  // Breakdowns and acquisition
  'App version': 'Verzija aplikacije',
  'Build number': 'Broj build-a',
  'Device model': 'Model uređaja',
  'Android version': 'Verzija Androida',
  'Country': 'Zemlja',
  'City': 'Grad',
  'Acquisition source': 'Izvor pridobijanja',
  'Install channel': 'Kanal instalacije',
  'Account type': 'Vrsta naloga',
  'Manual install (adb / flutter run)': 'Ručna instalacija (adb / flutter run)',
  'Package installer (sideload)': 'Instaler paketa (sideload)',
  '(not attributed)': '(bez atribucije)',
  '(not attributed) / -': '(bez atribucije) / -',
  '(unknown)': '(nepoznato)',
  '(not reported)': '(nije prijavljeno)',
  '(no title)': '(bez naslova)',
  'unknown': 'nepoznato',
  'guest': 'gost',
  'registered': 'registrovan',
  'Other': 'Ostalo',
  'Serbia': 'Srbija',
  'Belgrade': 'Beograd',
  'Nis': 'Niš',
  'AI / enrichment': 'AI / obogaćivanje',
  'The app also sends a `source` event parameter for receipts and shopping items, and GA4 reads that name as a campaign source. Session-level source reports in the GA4 interface can therefore be skewed; this dashboard uses only the user-level first-touch source.':
    'Aplikacija za račune i stavke liste šalje i parametar događaja `source`, a GA4 to ime čita kao izvor kampanje. Izveštaji o izvoru na nivou sesije u GA4 interfejsu zato mogu biti iskrivljeni; ova kontrolna tabla koristi samo izvor prvog kontakta na nivou korisnika.',
  'Mock note: session-level source reports can be skewed by the `source` event parameter; only user-level first touch is used here.':
    'Probna napomena: izveštaji o izvoru na nivou sesije mogu biti iskrivljeni parametrom događaja `source`; ovde se koristi samo prvi kontakt na nivou korisnika.',
  'Source is the GA4 first-touch traffic source, filled from the Google Play install referrer. Devices without a delivered referrer show as not attributed.':
    'Izvor je GA4 izvor saobraćaja pri prvom kontaktu, popunjen iz Google Play referera instalacije. Uređaji bez isporučenog referera prikazani su kao bez atribucije.',
  'Google Play Console acquisition data (store listing visitors, installs by channel and country) is not exported to BigQuery for this project, so store installs cannot be compared with first_open here.':
    'Podaci o pridobijanju iz Google Play Console (posetioci stranice u prodavnici, instalacije po kanalu i zemlji) ne izvoze se u BigQuery za ovaj projekat, pa se instalacije iz prodavnice ovde ne mogu porediti sa first_open.',
  'Google Play Console acquisition data is not exported to BigQuery for this project, so store installs cannot be compared with first_open here.':
    'Podaci o pridobijanju iz Google Play Console ne izvoze se u BigQuery za ovaj projekat, pa se instalacije iz prodavnice ovde ne mogu porediti sa first_open.',
  'No Play Console BigQuery export or Reporting API access is configured. Linking it needs a Play Console admin and a new service account permission.':
    'Nije podešen ni BigQuery izvoz iz Play Console ni pristup Reporting API-ju. Za povezivanje su potrebni administrator Play Console naloga i nova dozvola za servisni nalog.',
  'No Play Console BigQuery export or Reporting API access is configured.': 'Nije podešen ni BigQuery izvoz iz Play Console ni pristup Reporting API-ju.',
  'Installed outside Google Play (sideload or adb)': 'Instalirano mimo Google Play prodavnice (sideload ili adb)',
  'Build marked as internal testing': 'Build označen za interno testiranje',
  'GA4 debug mode': 'GA4 režim za otklanjanje grešaka',
  'Listed in the test-device registry': 'Upisan u registar test uređaja',

  // Activation and registration
  'Installed and opened': 'Instalirano i otvoreno',
  'Reached a first screen': 'Stigao do prvog ekrana',
  'Took a first meaningful action': 'Izvršio prvu smislenu radnju',
  'No install is a full day old yet': 'Nijedna instalacija još nije stara ceo dan',
  'No install is a full week old yet': 'Nijedna instalacija još nije stara celu nedelju',
  'No install has a final next day yet': 'Nijedna instalacija još nema završen naredni dan',
  'No device reached this step yet': 'Nijedan uređaj još nije stigao do ovog koraka',
  'The cohort is devices whose first_open fell in the selected range. Each device is followed up to now, including today. Guests and registered users are both in it; the split uses the last account type the device reported.':
    'Kohortu čine uređaji čiji je first_open u izabranom periodu. Svaki uređaj se prati do sada, uključujući danas. U njoj su i gosti i registrovani korisnici; podela koristi poslednju vrstu naloga koju je uređaj prijavio.',
  '"Reached a first screen" means the home screen was shown, which only happens after a guest or registered session exists. Installs that stop at the login or guest choice screen are the drop-off between the first two steps.':
    '„Stigao do prvog ekrana” znači da je prikazan početni ekran, što se dešava tek kada postoji sesija gosta ili registrovanog korisnika. Instalacije koje stanu na ekranu za prijavu ili izbor gosta su odustajanje između prva dva koraka.',
  'Activation within 24 hours or 7 days is judged only on installs that are old enough to have had that long. Younger installs are left out of both the count and the rate.':
    'Aktivacija u roku od 24 sata ili 7 dana ocenjuje se samo na instalacijama koje su dovoljno stare da su imale toliko vremena. Mlađe instalacije su izostavljene i iz broja i iz stope.',
  'Time to first action is measured only among devices that reached the step, so it excludes devices that never did.':
    'Vreme do prve radnje meri se samo na uređajima koji su stigli do tog koraka, pa ne obuhvata uređaje koji nikad nisu stigli.',
  'Time to first action is measured only among devices that reached the step.': 'Vreme do prve radnje meri se samo na uređajima koji su stigli do tog koraka.',
  'Shopping has one implicit list, so there is no separate "list created" event: opening the list and adding the first item are the two steps. Opening a feature is not activation; a first meaningful action is.':
    'Kupovina ima jednu podrazumevanu listu, pa ne postoji poseban događaj „lista napravljena”: otvaranje liste i dodavanje prve stavke su dva koraka. Otvaranje funkcije nije aktivacija; prva smislena radnja jeste.',
  'Mock data. The cohort is devices whose first_open fell in the selected range.': 'Probni podaci. Kohortu čine uređaji čiji je first_open u izabranom periodu.',
  'Started sign-up': 'Započeta registracija',
  'Completed sign-up': 'Završena registracija',
  'Verified': 'Potvrđeno',
  'Registration only: sign-up started, completed and verified (email verified, or a Google sign-in). Product use is in the install funnel above, which includes guests.':
    'Samo registracija: započeta, završena i potvrđena (potvrđena e-pošta ili prijava preko Google naloga). Korišćenje proizvoda je u levku instalacija iznad, koji uključuje i goste.',
  'Mock data. Registration only: sign-up started, completed and verified.': 'Probni podaci. Samo registracija: započeta, završena i potvrđena.',
  'No sign-up event has been recorded yet, so these steps cannot be measured.': 'Još nije zabeležen nijedan događaj registracije, pa se ovi koraci ne mogu izmeriti.',
  'A step is counted independently, so it can exceed the one before it (a sign-up with no recorded start). A rate above 100% is not shown.':
    'Svaki korak se broji nezavisno, pa može biti veći od prethodnog (registracija bez zabeleženog početka). Stopa veća od 100% se ne prikazuje.',

  // Retention and activity
  'app_remove is a GA4 signal sent when Android reports the app removed. It is not confirmed churn: it can arrive late or be missed, and a reinstall starts a new device id, so the same person can appear as a new install.':
    'app_remove je GA4 signal koji se šalje kada Android prijavi da je aplikacija uklonjena. To nije potvrđen odlazak: može da stigne kasno ili da izostane, a ponovna instalacija dobija novi id uređaja, pa se ista osoba može pojaviti kao nova instalacija.',
  'No install cohort has reached this day yet': 'Nijedna kohorta instalacija još nije stigla do ovog dana',
  'No installs in the selected cohorts': 'Nema instalacija u izabranim kohortama',
  'Needs at least one active device in the last 30 days': 'Potreban je bar jedan aktivan uređaj u poslednjih 30 dana',
  '1 day': '1 dan',
  '2 days': '2 dana',
  '3-4 days': '3-4 dana',
  '5-7 days': '5-7 dana',
  '8+ days': '8+ dana',
  '0-1 days ago': 'pre 0-1 dan',
  '2-7 days ago': 'pre 2-7 dana',
  '8-14 days ago': 'pre 8-14 dana',
  '15+ days ago': 'pre 15+ dana',

  // Reliability
  'Crashlytics export has no data for this range': 'Crashlytics izvoz nema podataka za ovaj period',
  'Firebase Sessions export has no data for this range': 'Firebase Sessions izvoz nema podataka za ovaj period',
  'Needs the Firebase Sessions export to map crashes to installations': 'Potreban je Firebase Sessions izvoz da bi se padovi povezali sa instalacijama',

  // Costs and receipts
  'Not logged: the backend analytics log carries no page index, so a first browse page cannot be told apart from a load-more':
    'Ne beleži se: analitički dnevnik bekenda nema indeks strane, pa se prva strana pregleda ne može razlikovati od „Učitaj još”',
  'AI billing rows are not available': 'Redovi obračuna za AI nisu dostupni',
  'Removed: dividing the whole cloud bill by receipts was misleading. See the metered AI cost per image receipt.':
    'Uklonjeno: deljenje celog računa za oblak brojem računa je navodilo na pogrešan zaključak. Pogledaj izmereni AI trošak po računu sa fotografije.',
  'Removed: dividing the whole cloud bill by searches was misleading.': 'Uklonjeno: deljenje celog računa za oblak brojem pretraga je navodilo na pogrešan zaključak.',
  'Cost: Cloud Billing export, services Gemini API, Vertex AI and Generative Language API. Calls: backend analytics log (receipt enrichment).':
    'Trošak: Cloud Billing izvoz, servisi Gemini API, Vertex AI i Generative Language API. Pozivi: analitički dnevnik bekenda (obogaćivanje računa).',
  'Mock data. Cost: Cloud Billing export. Calls: backend analytics log.': 'Probni podaci. Trošak: Cloud Billing izvoz. Pozivi: analitički dnevnik bekenda.',
  'receipt facts have not been built yet': 'činjenice o računima još nisu izgrađene',

  // Source readiness (warehouse)
  'source table is not configured': 'tabela izvora nije podešena',
  'source metadata table is not configured': 'tabela metapodataka izvora nije podešena',
  'source table is not available': 'tabela izvora nije dostupna',
  'source access denied: the analytics service account has no read access to this dataset': 'pristup izvoru je odbijen: analitički servisni nalog nema pravo čitanja ovog skupa podataka',
  'source query failed': 'upit nad izvorom nije uspeo',
  'query failed': 'upit nije uspeo',

  // Request errors
  'Admin access required.': 'Potreban je administratorski pristup.',
  'Analytics request failed.': 'Zahtev za analitiku nije uspeo.',
  'Analytics request failed': 'Zahtev za analitiku nije uspeo',
  'start and end must be valid YYYY-MM-DD dates in the reporting timezone': 'početak i kraj moraju biti ispravni datumi u obliku GGGG-MM-DD u izveštajnoj vremenskoj zoni',
  'end must be on or after start': 'kraj ne sme biti pre početka',
  'date range cannot exceed 366 inclusive days': 'period ne može biti duži od 366 dana',
  'scope must be filtered or raw': 'obuhvat mora biti filtered ili raw',
  'segment must be all, guest or registered': 'segment mora biti all, guest ili registered',
  'unable to resolve reporting date': 'izveštajni datum nije moguće odrediti',
};

const what = (word: string) => (word === 'users' ? 'korisnika' : word === 'sessions' ? 'sesija' : word);
const crashed = (word: string) => (word === 'users' ? 'korisnici sa padom' : word === 'sessions' ? 'sesije sa padom' : word);
const sr = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]!}.${m[2]!}.${m[1]!}.` : iso;
};

/** Text with variable parts (dates, counts, names). The first matching pattern wins. */
export const patterns: Array<[RegExp, (match: RegExpExecArray) => string]> = [
  [/^Other \(fewer than (\d+) devices\)$/, (m) => `Ostalo (manje od ${m[1]!} uređaja)`],
  [/^Source is the GA4 first-touch traffic source, filled from the Google Play install referrer\. Devices without a delivered referrer show as not attributed(?: \((\d+) in this range\))?\.$/,
    (m) => `Izvor je GA4 izvor saobraćaja pri prvom kontaktu, popunjen iz Google Play referera instalacije. Uređaji bez isporučenog referera prikazani su kao bez atribucije${m[1] ? ` (${m[1]!} u ovom periodu)` : ''}.`],
  [/^Sign-up events exist in the data from (\d{4}-\d{2}-\d{2})\. Accounts created before that day are not in these counts; the account totals on the Overview come from Firebase Auth and include them\.$/,
    (m) => `Događaji registracije postoje u podacima od ${sr(m[1]!)} Nalozi otvoreni pre tog dana nisu u ovim brojevima; ukupan broj naloga na Pregledu dolazi iz Firebase Auth i uključuje ih.`],
  [/^Sign-up events exist in the data from (\d{4}-\d{2}-\d{2})\.$/, (m) => `Događaji registracije postoje u podacima od ${sr(m[1]!)}`],
  [/^The (.+) export starts on (\d{4}-\d{2}-\d{2})\. Choose a range that starts on or after that day\.$/,
    (m) => `Izvoz ${m[1]!} počinje ${sr(m[2]!)} Izaberi period koji počinje tog dana ili kasnije.`],
  [/^No (users|sessions) recorded in this range$/, (m) => `U ovom periodu nema zabeleženih ${what(m[1]!)}`],
  [/^Crashed (users|sessions) cannot be derived: the sessions export is not configured$/, (m) => `Nije moguće izračunati ${crashed(m[1]!)}: izvoz sesija nije podešen`],
  [/^Week of (\d{4}-\d{2}-\d{2})$/, (m) => `Nedelja od ${sr(m[1]!)}`],
];
